import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
const ContactPopup = lazy(() => import("@/components/ContactPopup"));
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy load PageTracker to defer Supabase SDK loading
const PageTracker = lazy(() => import("@/hooks/usePageTracking").then(m => ({ default: m.PageTracker })));

// Lazy load service pages - not needed on initial page load
const Valuation = lazyWithRetry(() => import("./pages/servicos/Valuation"));
const MA = lazyWithRetry(() => import("./pages/servicos/MA"));
const FinanceOffice = lazyWithRetry(() => import("./pages/servicos/FinanceOffice"));
const Auditoria = lazyWithRetry(() => import("./pages/servicos/Auditoria"));

// Lazy load blog pages
const Blog = lazyWithRetry(() => import("./pages/blog/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/blog/BlogPost"));

// Lazy load admin pages
const AdminLayout = lazyWithRetry(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminPosts = lazyWithRetry(() => import("./pages/admin/AdminPosts"));
const AdminPostNew = lazyWithRetry(() => import("./pages/admin/AdminPostNew"));
const AdminPostEdit = lazyWithRetry(() => import("./pages/admin/AdminPostEdit"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminAnalyticsDebug = lazyWithRetry(() => import("./pages/admin/AdminAnalyticsDebug"));
const AdminLeads = lazyWithRetry(() => import("./pages/admin/AdminLeads"));
const AdminLogin = lazyWithRetry(() => import("./pages/admin/AdminLogin"));

// Lazy load ProtectedRoute to avoid loading auth code on public site
const ProtectedRoute = lazyWithRetry(() => import("./components/auth/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background" />
);

const useIdleRender = (delayMs = 0) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const schedule = () => {
      const requestIdleCallback =
        (window as Window & {
          requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        }).requestIdleCallback;

      if (requestIdleCallback) {
        requestIdleCallback(() => setReady(true), { timeout: 1500 });
      } else {
        setTimeout(() => setReady(true), delayMs);
      }
    };

    if (document.readyState === "complete") {
      schedule();
      return;
    }

    window.addEventListener("load", schedule, { once: true });
    return () => window.removeEventListener("load", schedule);
  }, [delayMs]);

  return ready;
};

const DeferredPageTracker = () => {
  const shouldRender = useIdleRender(800);

  if (!shouldRender) return null;

  return (
    <Suspense fallback={null}>
      <PageTracker />
    </Suspense>
  );
};

const DeferredContactPopup = () => {
  const shouldRender = useIdleRender(1200);

  if (!shouldRender) return null;

  return (
    <Suspense fallback={null}>
      <ContactPopup />
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <DeferredPageTracker />
          <DeferredContactPopup />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/servicos/valuation" element={
              <Suspense fallback={<PageLoader />}>
                <Valuation />
              </Suspense>
            } />
            <Route path="/servicos/ma" element={
              <Suspense fallback={<PageLoader />}>
                <MA />
              </Suspense>
            } />
            <Route path="/servicos/finance-office" element={
              <Suspense fallback={<PageLoader />}>
                <FinanceOffice />
              </Suspense>
            } />
            <Route path="/servicos/auditoria" element={
              <Suspense fallback={<PageLoader />}>
                <Auditoria />
              </Suspense>
            } />
            <Route path="/blog" element={
              <Suspense fallback={<PageLoader />}>
                <Blog />
              </Suspense>
            } />
            <Route path="/blog/:slug" element={
              <Suspense fallback={<PageLoader />}>
                <BlogPost />
              </Suspense>
            } />
            
            {/* Admin Login */}
            <Route path="/admin-login" element={
              <Suspense fallback={<PageLoader />}>
                <AdminLogin />
              </Suspense>
            } />
            
            {/* Admin Routes - Protected */}
            <Route path="/admin-blog-csp-2025" element={
              <Suspense fallback={<PageLoader />}>
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              </Suspense>
            }>
              <Route index element={
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard />
                </Suspense>
              } />
              <Route path="posts" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminPosts />
                </Suspense>
              } />
              <Route path="posts/new" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminPostNew />
                </Suspense>
              } />
              <Route path="posts/:id" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminPostEdit />
                </Suspense>
              } />
              <Route path="leads" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminLeads />
                </Suspense>
              } />
              <Route path="analytics" element={
                <Suspense fallback={<PageLoader />}>
                  <AdminAnalytics />
                </Suspense>
              } />
              {import.meta.env.DEV && (
                <Route path="analytics-debug" element={
                  <Suspense fallback={<PageLoader />}>
                    <AdminAnalyticsDebug />
                  </Suspense>
                } />
              )}
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
